import React, { useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { PaydayRule, PaydayAllocation } from '../types';
import { formatCurrency, formatDateShort } from '../utils/calculations';

const PaydayRules: React.FC = () => {
  const { state } = useAppContext();
  const [showAddRow, setShowAddRow] = useState(false);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Payday Rules</h2>
          <button
            onClick={() => setShowAddRow(true)}
            className="btn-primary"
            disabled={showAddRow}
          >
            Add Rule
          </button>
        </div>
        
        {state.paydayRules.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            No payday rules configured. Add a rule to automatically distribute money to your pots on payday.
          </p>
        ) : (
          (() => {
            // Group rules by day of month
            const rulesByDay = new Map<number | 'LDOM', typeof state.paydayRules>();
            state.paydayRules.forEach(rule => {
              if (!rulesByDay.has(rule.dayOfMonth)) {
                rulesByDay.set(rule.dayOfMonth, []);
              }
              rulesByDay.get(rule.dayOfMonth)!.push(rule);
            });

            // Sort days numerically, with LDOM at the end
            const sortedDays = Array.from(rulesByDay.keys()).sort((a, b) => {
              if (a === 'LDOM') return 1;
              if (b === 'LDOM') return -1;
              return (a as number) - (b as number);
            });

            return (
              <div className="space-y-4">
                {sortedDays.map(dayOfMonth => {
                  const rulesForDay = rulesByDay.get(dayOfMonth)!;
                  const totalAllocation = rulesForDay.reduce((sum, rule) => 
                    sum + rule.allocations.reduce((ruleSum, allocation) => ruleSum + allocation.amount, 0), 0
                  );

                  return (
                    <div key={dayOfMonth} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-800">
                          📅 {dayOfMonth === 'LDOM'
                            ? 'Last Day of Each Month'
                            : `${dayOfMonth}${dayOfMonth === 1 ? 'st' : dayOfMonth === 2 ? 'nd' : dayOfMonth === 3 ? 'rd' : 'th'} of Each Month`
                          }
                        </h3>
                        <p className="text-sm text-gray-600">
                          Total allocation: {formatCurrency(totalAllocation)}
                        </p>
                      </div>
                      
                      <div className="table-container payments-table">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th className="text-left">Allocations</th>
                              <th className="text-right">Amount</th>
                              <th className="text-center">Date Range</th>
                              <th className="text-center" style={{ width: '160px' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rulesForDay.map(rule => (
                              <PaydayRuleTableRow key={rule.id} rule={rule} />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()
        )}

        {showAddRow && (
          <AddPaydayRuleForm onClose={() => setShowAddRow(false)} />
        )}
      </div>
    </div>
  );
};

interface PaydayRuleTableRowProps {
  rule: PaydayRule;
}

const PaydayRuleTableRow: React.FC<PaydayRuleTableRowProps> = ({ rule }) => {
  const { state, dispatch } = useAppContext();
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    dayOfMonth: rule.dayOfMonth.toString(),
    allocations: rule.allocations.map(a => ({ ...a })),
    startDate: rule.startDate ? rule.startDate.toISOString().split('T')[0] : '',
    endDate: rule.endDate ? rule.endDate.toISOString().split('T')[0] : ''
  });

  const toggleRule = (isActive: boolean) => {
    dispatch({
      type: 'UPDATE_PAYDAY_RULE',
      payload: { id: rule.id, rule: { isActive } }
    });
  };

  const deleteRule = () => {
    if (window.confirm('Are you sure you want to delete this payday rule?')) {
      dispatch({ type: 'DELETE_PAYDAY_RULE', payload: rule.id });
    }
  };

  const startEditing = () => {
    setIsEditing(true);
    setEditForm({
      dayOfMonth: rule.dayOfMonth.toString(),
      allocations: rule.allocations.map(a => ({ ...a })),
      startDate: rule.startDate ? rule.startDate.toISOString().split('T')[0] : '',
      endDate: rule.endDate ? rule.endDate.toISOString().split('T')[0] : ''
    });
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditForm({
      dayOfMonth: rule.dayOfMonth.toString(),
      allocations: rule.allocations.map(a => ({ ...a })),
      startDate: rule.startDate ? rule.startDate.toISOString().split('T')[0] : '',
      endDate: rule.endDate ? rule.endDate.toISOString().split('T')[0] : ''
    });
  };

  const saveEdit = () => {
    dispatch({
      type: 'UPDATE_PAYDAY_RULE',
      payload: {
        id: rule.id,
        rule: {
          dayOfMonth: editForm.dayOfMonth === 'LDOM' ? 'LDOM' : parseInt(editForm.dayOfMonth),
          allocations: editForm.allocations,
          isActive: rule.isActive,
          startDate: editForm.startDate ? new Date(editForm.startDate) : undefined,
          endDate: editForm.endDate ? new Date(editForm.endDate) : undefined
        }
      }
    });
    setIsEditing(false);
  };

  const updateAllocation = (index: number, field: keyof PaydayAllocation, value: any) => {
    const updated = [...editForm.allocations];
    if (field === 'amount') {
      updated[index] = { ...updated[index], [field]: parseFloat(value) || 0 };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setEditForm({ ...editForm, allocations: updated });
  };

  const removeAllocation = (index: number) => {
    const updated = editForm.allocations.filter((_, i) => i !== index);
    setEditForm({ ...editForm, allocations: updated });
  };

  const addAllocation = () => {
    if (state.pots.length === 0) return;

    const newAllocation = {
      id: Date.now().toString(36) + Math.random().toString(36).substr(2),
      potId: state.pots[0].id,
      amount: 0
    };
    setEditForm({
      ...editForm,
      allocations: [...editForm.allocations, newAllocation]
    });
  };

  const totalAllocation = rule.allocations.reduce((sum, allocation) => sum + allocation.amount, 0);

  if (isEditing) {
    // Since each rule should have exactly one allocation, we edit the first (and only) allocation
    const allocation = editForm.allocations[0];
    const pot = state.pots.find(p => p.id === allocation?.potId);

    return (
      <tr>
        <td className="text-left">
          <div className="flex items-center">
            {pot && (
              <div
                className="w-4 h-4 rounded-full border border-gray-400 mr-2 flex-shrink-0"
                style={{ backgroundColor: pot.color, minWidth: '16px', minHeight: '16px' }}
              />
            )}
            <select
              value={allocation?.potId || ''}
              onChange={(e) => updateAllocation(0, 'potId', e.target.value)}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
            >
              {state.pots.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        </td>
        <td className="text-right">
          <input
            type="number"
            step="0.01"
            value={allocation?.amount || 0}
            onChange={(e) => updateAllocation(0, 'amount', e.target.value)}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded text-right"
          />
        </td>
        <td className="text-center">
          <div className="space-y-1">
            <input
              type="date"
              value={editForm.startDate || ''}
              onChange={(e) => setEditForm({ ...editForm, startDate: e.target.value })}
              className="w-32 px-2 py-1 text-sm border border-gray-300 rounded"
              placeholder="Start date"
            />
            <input
              type="date"
              value={editForm.endDate || ''}
              onChange={(e) => setEditForm({ ...editForm, endDate: e.target.value })}
              className="w-32 px-2 py-1 text-sm border border-gray-300 rounded"
              placeholder="End date"
            />
          </div>
        </td>
        <td className="text-center">
          <div className="flex space-x-1 justify-center">
            <button
              onClick={saveEdit}
              className="btn-secondary text-xs px-2 py-1"
            >
              Save
            </button>
            <button
              onClick={cancelEditing}
              className="btn-neutral text-xs px-2 py-1"
            >
              Cancel
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td className="text-left">
        <div className="space-y-1">
          {rule.allocations.map(allocation => {
            const pot = state.pots.find(p => p.id === allocation.potId);
            return (
              <div key={allocation.id} className="flex items-center">
                {pot && (
                  <div
                    className="w-4 h-4 rounded-full border border-gray-400 mr-2 flex-shrink-0"
                    style={{ backgroundColor: pot.color, minWidth: '16px', minHeight: '16px' }}
                  />
                )}
                <span className="text-gray-700">{pot ? pot.name : 'Unknown Pot'}</span>
              </div>
            );
          })}
        </div>
      </td>
      <td className="text-right font-semibold text-gray-800">
        {formatCurrency(totalAllocation)}
      </td>
      <td className="text-center text-gray-600">
        <div className="space-y-1 text-xs">
          <div>{rule.startDate ? `From: ${formatDateShort(rule.startDate)}` : 'Always active'}</div>
          <div>{rule.endDate ? `Until: ${formatDateShort(rule.endDate)}` : 'No end date'}</div>
        </div>
      </td>
      <td className="text-center">
        <div className="flex space-x-1 justify-center">
          <button
            onClick={startEditing}
            className="btn-secondary text-xs px-2 py-1"
          >
            Edit
          </button>
          <button
            onClick={deleteRule}
            className="btn-danger text-xs px-2 py-1"
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
};

interface AddPaydayRuleFormProps {
  onClose: () => void;
}

const AddPaydayRuleForm: React.FC<AddPaydayRuleFormProps> = ({ onClose }) => {
  const { state, dispatch } = useAppContext();
  const [dayOfMonth, setDayOfMonth] = useState('1');
  const [allocations, setAllocations] = useState<Omit<PaydayAllocation, 'id'>[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const addAllocation = () => {
    if (state.pots.length === 0) {
      alert('Please create at least one pot before adding allocations.');
      return;
    }

    setAllocations([
      ...allocations,
      {
        potId: state.pots[0].id,
        amount: 0
      }
    ]);
  };

  const updateAllocation = (index: number, field: keyof Omit<PaydayAllocation, 'id'>, value: any) => {
    const updated = [...allocations];
    updated[index] = { ...updated[index], [field]: value };
    setAllocations(updated);
  };

  const removeAllocation = (index: number) => {
    setAllocations(allocations.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (allocations.length === 0) {
      alert('Please add at least one allocation.');
      return;
    }

    const rule: Omit<PaydayRule, 'id'> = {
      dayOfMonth: dayOfMonth === 'LDOM' ? 'LDOM' : parseInt(dayOfMonth),
      allocations: allocations.map(allocation => ({
        ...allocation,
        id: Date.now().toString(36) + Math.random().toString(36).substr(2)
      })),
      isActive: true,
      startDate: startDate ? new Date(startDate) : undefined,
      endDate: endDate ? new Date(endDate) : undefined
    };

    dispatch({ type: 'ADD_PAYDAY_RULE', payload: rule });
    onClose();
  };

  return (
    <div className="mt-6 bg-gray-50 rounded-lg p-6 border-2 border-dashed border-gray-300">
      <h3 className="text-lg font-semibold mb-4 text-gray-800">Add Payday Rule</h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payday (Day of Month)
            </label>
            <select
              required
              value={dayOfMonth}
              onChange={(e) => setDayOfMonth(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                <option key={day} value={day.toString()}>
                  {day}
                </option>
              ))}
              <option value="LDOM">Last Day of Month</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date (Optional)
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date (Optional)
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-end">
            <button
              type="button"
              onClick={addAllocation}
              className="w-full btn-success text-sm"
            >
              Add Allocation
            </button>
          </div>
        </div>

        {allocations.length === 0 ? (
          <div className="text-center py-6 bg-white rounded border border-gray-200">
            <p className="text-gray-500 text-sm">
              No allocations added. Click "Add Allocation" to start.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded border border-gray-200">
            <div className="table-container payments-table">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="text-left">Pot</th>
                    <th className="text-right">Amount (£)</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allocations.map((allocation, index) => (
                    <tr key={index}>
                      <td className="text-left">
                        <div className="flex items-center">
                          {(() => {
                            const selectedPot = state.pots.find(p => p.id === allocation.potId);
                            return selectedPot ? (
                              <div
                                className="w-4 h-4 rounded-full border border-gray-400 mr-2 flex-shrink-0"
                                style={{ backgroundColor: selectedPot.color, minWidth: '16px', minHeight: '16px' }}
                              />
                            ) : null;
                          })()}
                          <select
                            value={allocation.potId}
                            onChange={(e) => updateAllocation(index, 'potId', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            {state.pots.map(pot => (
                              <option key={pot.id} value={pot.id}>
                                {pot.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>
                      <td className="text-right">
                        <input
                          type="number"
                          step="0.01"
                          value={allocation.amount}
                          onChange={(e) => updateAllocation(index, 'amount', parseFloat(e.target.value) || 0)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                        />
                      </td>
                      <td className="text-center">
                        <button
                          type="button"
                          onClick={() => removeAllocation(index)}
                          className="btn-danger text-xs px-2 py-1"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex space-x-3 pt-4">
          <button
            type="submit"
            className="flex-1 btn-primary"
          >
            Add Rule
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 btn-danger"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default PaydayRules;